import { Gavel, Lock, Plus, RefreshCw, RotateCcw, Scale } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useSearchParams } from '@/lib/scopedParams';
import { api, type Dispute, type DisputeStatus, type DisputeType, type NewDispute } from '@/api';
import { Badge } from '@/components/Badge';
import { Callout, DataTable, Field, FilterBar, Section, StatCard, StatGrid, type Column } from '@/components/blocks';
import { DataError } from '@/components/DataError';
import { EntityRef } from '@/components/EntityRef';
import { PageHeader, useDocumentTitle } from '@/components/PageHeader';
import { EmptyState, Loading } from '@/components/States';
import { useWrite, WriteError } from '@/components/Write';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { formatCount } from '@/lib/format';
import { useApi } from '@/lib/useApi';
import { ActionDialog, ConfirmCheck, EnumBadge, FormActions, FormGrid, MfaHint, SelectField, useEnumLabel, useEnumOptions, When } from './ui';
import { DISPUTE_STATUS, DISPUTE_TYPES } from './vocab';

interface OpenForm {
  domain: string;
  dispute_type: DisputeType;
  provider: string;
  case_reference: string;
  summary: string;
  abuse_case_id: string;
}

type DisputeRow = Dispute;

function OpenDisputeForm({ onCancel }: { onCancel: () => void }) {
  const { t } = useTranslation('compliance');
  const { t: tc } = useTranslation();
  const options = useEnumOptions();
  const navigate = useNavigate();
  const [f, setF] = useState<OpenForm>({
    domain: '',
    dispute_type: 'UDRP',
    provider: '',
    case_reference: '',
    summary: '',
    abuse_case_id: '',
  });
  const [confirm, setConfirm] = useState(false);
  // The API takes a domain UUID; resolve the name first (the admin API accepts either as a key).
  const write = useWrite(async (req: OpenForm, opts) => {
    const detail = await api.getDomain(req.domain.trim(), {
      signal: opts.signal,
    });
    const body: NewDispute = {
      domain_id: detail.domain.id,
      dispute_type: req.dispute_type,
      provider: req.provider.trim(),
      case_reference: req.case_reference.trim(),
      summary: req.summary.trim() || null,
      abuse_case_id: req.abuse_case_id.trim() || null,
    };
    return api.openDispute(body, opts);
  });
  const valid = f.domain.trim() && f.provider.trim() && f.case_reference.trim() && confirm;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!valid) return;
    const res = await write.run(f);
    if (res) navigate(`/compliance/icann/disputes/${encodeURIComponent(res.id)}`);
  };

  return (
    <form onSubmit={submit} className="grid gap-4">
      <FormGrid>
        <Field label={t('disputes.form.domain')}>
          <Input value={f.domain} onChange={(e) => setF({ ...f, domain: e.target.value })} disabled={write.busy} spellCheck={false} />
        </Field>
        <SelectField
          label={t('disputes.form.type')}
          value={f.dispute_type}
          options={options('disputeType', DISPUTE_TYPES)}
          onChange={(v) => v && setF({ ...f, dispute_type: v })}
          disabled={write.busy}
        />
        <Field label={t('disputes.form.provider')}>
          <Input value={f.provider} onChange={(e) => setF({ ...f, provider: e.target.value })} disabled={write.busy} placeholder="WIPO" />
        </Field>
        <Field label={t('disputes.form.reference')}>
          <Input value={f.case_reference} onChange={(e) => setF({ ...f, case_reference: e.target.value })} disabled={write.busy} />
        </Field>
        <Field label={t('disputes.form.abuseCase')} className="sm:col-span-2">
          <Input value={f.abuse_case_id} onChange={(e) => setF({ ...f, abuse_case_id: e.target.value })} disabled={write.busy} className="font-mono" />
        </Field>
        <Field label={t('disputes.form.summary')} className="sm:col-span-2">
          <Textarea rows={2} value={f.summary} onChange={(e) => setF({ ...f, summary: e.target.value })} disabled={write.busy} />
        </Field>
      </FormGrid>
      <ConfirmCheck checked={confirm} onChange={setConfirm} disabled={write.busy}>
        {t('disputes.form.confirm')}
      </ConfirmCheck>
      <MfaHint permission="dispute.manage" />
      {write.state.phase === 'error' ? <WriteError error={write.state.error} /> : null}
      <FormActions>
        <Button type="button" variant="outline" onClick={onCancel} disabled={write.busy}>
          {tc('action.cancel')}
        </Button>
        <Button type="submit" disabled={write.busy || !valid}>
          <Lock /> {write.busy ? t('disputes.form.busy') : t('disputes.form.submit')}
        </Button>
      </FormActions>
    </form>
  );
}

/** `/compliance/icann/disputes`: UDRP / URS, court orders and other disputes with their legal locks (spec §45). */
export function DisputesPage() {
  const { t } = useTranslation('compliance');
  const { t: tc } = useTranslation();
  const label = useEnumLabel();
  const options = useEnumOptions();
  useDocumentTitle(t('disputes.list.title'));
  const [params, setParams] = useSearchParams();
  const status = (params.get('status') ?? '') as DisputeStatus | '';
  const [creating, setCreating] = useState(false);
  const result = useApi((signal) => api.listDisputes({ status: status || undefined, limit: 200 }, { signal }), [status]);
  const items = result.status === 'success' ? result.data.items : [];
  const openItems = items.filter((x) => x.status !== 'CLOSED' && x.status !== 'WITHDRAWN');

  const columns: Column<DisputeRow>[] = [
    {
      key: 'domain',
      header: t('disputes.list.col.domain'),
      cell: (x) => <EntityRef label={x.domain} to={x.domain ? `/domains/${encodeURIComponent(x.domain)}` : undefined} id={x.domain_id} />,
    },
    {
      key: 'type',
      header: t('disputes.list.col.type'),
      cell: (x) => (
        <>
          <span title={x.dispute_type}>{label('disputeType', x.dispute_type)}</span>
          <div className="text-xs text-muted-foreground">{x.provider}</div>
        </>
      ),
    },
    {
      key: 'ref',
      header: t('disputes.list.col.reference'),
      cell: (x) => <EntityRef label={x.case_reference} to={`/compliance/icann/disputes/${encodeURIComponent(x.id)}`} id={x.id} />,
    },
    {
      key: 'status',
      header: t('disputes.list.col.status'),
      cell: (x) => (
        <>
          <EnumBadge group="disputeStatus" value={x.status} tones={DISPUTE_STATUS} />
          {x.decision ? <div className="mt-1 text-xs text-muted-foreground">{label('disputeDecision', x.decision)}</div> : null}
        </>
      ),
    },
    {
      key: 'lock',
      header: t('disputes.list.col.lock'),
      cell: (x) => (
        <Badge tone={x.lock_active ? 'warn' : 'neutral'} title={x.lock_type}>
          {label('lockType', x.lock_type)} · {x.lock_active ? t('disputes.list.lockActive') : t('disputes.list.lockReleased')}
        </Badge>
      ),
    },
    {
      key: 'opened',
      header: t('disputes.list.col.opened'),
      cell: (x) => <When at={x.opened_at} />,
    },
  ];

  return (
    <>
      <PageHeader
        title={t('disputes.list.title')}
        crumbs={[{ label: t('crumbs.compliance') }, { label: t('crumbs.icann') }]}
        subtitle={t('disputes.list.subtitle')}
        actions={
          <>
            <Button size="sm" onClick={() => setCreating(true)}>
              <Plus /> {t('disputes.list.open')}
            </Button>
            <Button size="sm" variant="outline" onClick={result.reload}>
              <RefreshCw /> {tc('action.refresh')}
            </Button>
          </>
        }
      />
      <ActionDialog open={creating} onOpenChange={setCreating} title={t('disputes.form.title')} description={t('disputes.form.description')} wide>
        <OpenDisputeForm onCancel={() => setCreating(false)} />
      </ActionDialog>
      <div className="space-y-6">
        {result.status === 'success' ? (
          <StatGrid className="xl:grid-cols-3">
            <StatCard label={t('disputes.list.stats.open')} value={formatCount(openItems.length)} icon={Scale} />
            <StatCard
              label={t('disputes.list.stats.locked')}
              value={formatCount(items.filter((x) => x.lock_active).length)}
              tone={items.some((x) => x.lock_active) ? 'warn' : 'neutral'}
              icon={Lock}
            />
            <StatCard
              label={t('disputes.list.stats.decided')}
              value={formatCount(items.filter((x) => x.status === 'DECIDED').length)}
              tone={items.some((x) => x.status === 'DECIDED') ? 'pending' : 'neutral'}
              icon={Gavel}
            />
          </StatGrid>
        ) : null}
        <Callout tone="info">{t('disputes.detail.closeHint')}</Callout>
        <Section flush>
          <FilterBar
            className="px-5"
            actions={
              status ? (
                <Button variant="ghost" size="sm" onClick={() => setParams(new URLSearchParams())}>
                  <RotateCcw /> {tc('action.reset')}
                </Button>
              ) : null
            }
          >
            <SelectField
              label={t('common.status')}
              value={status}
              allLabel={t('common.any')}
              options={options('disputeStatus', Object.keys(DISPUTE_STATUS) as DisputeStatus[])}
              onChange={(v) => {
                const p = new URLSearchParams(params);
                if (v) p.set('status', v);
                else p.delete('status');
                setParams(p);
              }}
              className="w-full sm:w-52"
            />
          </FilterBar>
          {result.status === 'loading' ? (
            <div className="px-5">
              <Loading rows={4} />
            </div>
          ) : null}
          {result.status === 'error' ? (
            <div className="px-5 pb-5">
              <DataError error={result.error} onRetry={result.reload} title={t('disputes.list.loadError')} />
            </div>
          ) : null}
          {result.status === 'success' ? <DataTable columns={columns} rows={items} rowKey={(x) => x.id} empty={<EmptyState title={t('disputes.list.empty')} />} /> : null}
        </Section>
      </div>
    </>
  );
}
